import { useMemo, useState } from 'react';
import type { ComposerDocument, Block } from '../types';
import { CATALOG, getDefinition } from '../components/catalog';
import { findBlock, findParent, makeBlock } from '../state/tree';
import { findTheme, groupThemes, type ThemeEntry } from '../themes';

/* ==========================================================================
   Right-side inspector panel.
   --------------------------------------------------------------------------
   Renders one of three states:
     1. A Block is selected -> show its prop schema as a form, plus
        block-level actions (move up/down, duplicate, delete).
        Container blocks also expose an "Add child" picker.
     2. Nothing is selected -> show document-level controls:
        name, theme, color overrides, and a one-click "Add component"
        sheet to drop a new top-level block.
   ========================================================================== */

interface Props {
  doc: ComposerDocument;
  selectedId: string | null;
  onPatchDocument: (patch: Partial<ComposerDocument>) => void;
  onUpdateBlockProps: (id: string, patch: Record<string, unknown>) => void;
  onAddRootBlock: (type: string) => void;
  onAddChildBlock: (parentId: string, type: string) => void;
  onDeleteBlock: (id: string) => void;
  onDuplicateBlock: (id: string) => void;
  onMoveBlock: (id: string, direction: -1 | 1) => void;
  themes: ThemeEntry[];
}

export function Inspector(props: Props) {
  const { doc, selectedId } = props;
  const block = selectedId ? findBlock(doc.tree, selectedId) : null;
  return (
    <aside className="cb-inspector">
      <header className="cb-inspector-head">
        <span className="cb-inspector-title">{block ? 'Component' : 'Document'}</span>
        {block && (
          <button className="cb-link" type="button"
            onClick={() => props.onPatchDocument({})}
            onMouseDown={() => null}
            >
            <span aria-hidden>×</span>
          </button>
        )}
      </header>
      {block ? <BlockInspector block={block} {...props} /> : <DocumentInspector {...props} />}
    </aside>
  );
}

function DocumentInspector({ doc, onPatchDocument, onAddRootBlock, themes }: Props) {
  const groups = Array.from(new Set(CATALOG.map((c) => c.group)));
  const overrides = doc.colorOverrides || {};
  return (
    <div className="cb-inspector-body">
      <Section label="Document">
        <Field label="Name">
          <input type="text" value={doc.name}
            onChange={(e) => onPatchDocument({ name: e.target.value })} />
        </Field>
        <ThemePickerField
          themes={themes}
          value={doc.theme}
          onChange={(v) => onPatchDocument({ theme: v })}
        />
      </Section>
      <Section label="Color overrides" help="Optional. Overrides the chosen theme.">
        <Field label="Accent">
          <input type="color" value={overrides.accent || '#d2492a'}
            onChange={(e) => onPatchDocument({ colorOverrides: { ...overrides, accent: e.target.value } })} />
          <button type="button" className="cb-link"
            onClick={() => onPatchDocument({ colorOverrides: { ...overrides, accent: undefined } })}>Clear</button>
        </Field>
        <Field label="Ink">
          <input type="color" value={overrides.ink || '#1c1d1f'}
            onChange={(e) => onPatchDocument({ colorOverrides: { ...overrides, ink: e.target.value } })} />
          <button type="button" className="cb-link"
            onClick={() => onPatchDocument({ colorOverrides: { ...overrides, ink: undefined } })}>Clear</button>
        </Field>
        <Field label="Paper">
          <input type="color" value={overrides.paper || '#ffffff'}
            onChange={(e) => onPatchDocument({ colorOverrides: { ...overrides, paper: e.target.value } })} />
          <button type="button" className="cb-link"
            onClick={() => onPatchDocument({ colorOverrides: { ...overrides, paper: undefined } })}>Clear</button>
        </Field>
      </Section>
      <Section label="Add component" help="Drop a new top-level block at the end of the page.">
        {groups.map((g) => (
          <div className="cb-cat-group" key={g}>
            <h5>{g}</h5>
            <div className="cb-cat-grid">
              {CATALOG.filter((c) => c.group === g && c.rootAllowed).map((c) => (
                <button key={c.type} type="button" className="cb-cat-item"
                  title={c.description}
                  onClick={() => onAddRootBlock(c.type)}>
                  <span className="cb-cat-name">{c.label}</span>
                  <span className="cb-cat-tier">{c.tier}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}

function BlockInspector(
  { block, doc, onUpdateBlockProps, onAddChildBlock, onDeleteBlock, onDuplicateBlock, onMoveBlock }:
  Props & { block: Block },
) {
  const def = getDefinition(block.type);
  if (!def) return <p>Unknown component.</p>;
  const parent = findParent(doc.tree, block.id)?.parent;
  return (
    <div className="cb-inspector-body">
      <Section label={def.label} help={def.description}>
        <div className="cb-actions">
          <button type="button" onClick={() => onMoveBlock(block.id, -1)}>↑ Move up</button>
          <button type="button" onClick={() => onMoveBlock(block.id, 1)}>↓ Move down</button>
          <button type="button" onClick={() => onDuplicateBlock(block.id)}>Duplicate</button>
          <button type="button" className="cb-danger" onClick={() => onDeleteBlock(block.id)}>Delete</button>
        </div>
      </Section>
      <Section label="Properties">
        {def.props.length === 0 && <p className="cb-help">This component has no properties.</p>}
        {def.props.filter((p) => p.type !== 'rich').map((p) => {
          const value = block.props[p.key];
          if (p.type === 'text') {
            return (
              <Field key={p.key} label={p.label} help={p.help}>
                <input type="text" value={String(value ?? '')}
                  onChange={(e) => onUpdateBlockProps(block.id, { [p.key]: e.target.value })} />
              </Field>
            );
          }
          if (p.type === 'number') {
            return (
              <Field key={p.key} label={p.label} help={p.help}>
                <input type="number" min={p.min} max={p.max} step={p.step ?? 1}
                  value={Number(value ?? 0)}
                  onChange={(e) => onUpdateBlockProps(block.id, { [p.key]: Number(e.target.value) })} />
              </Field>
            );
          }
          if (p.type === 'boolean') {
            return (
              <Field key={p.key} label={p.label} help={p.help}>
                <input type="checkbox" checked={Boolean(value)}
                  onChange={(e) => onUpdateBlockProps(block.id, { [p.key]: e.target.checked })} />
              </Field>
            );
          }
          if (p.type === 'select') {
            return (
              <Field key={p.key} label={p.label} help={p.help}>
                <select value={String(value ?? '')}
                  onChange={(e) => onUpdateBlockProps(block.id, { [p.key]: e.target.value })}>
                  {p.options?.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </Field>
            );
          }
          return null;
        })}
        {def.props.some((p) => p.type === 'rich') && (
          <p className="cb-help">Rich-text content edits inline on the canvas — click into the block to format.</p>
        )}
      </Section>
      {def.tier === 'container' && (
        <Section label="Children">
          <div className="cb-children-list">
            {(block.children || []).map((c, i) => (
              <div key={c.id} className="cb-children-item">
                <span>{i + 1}. {getDefinition(c.type)?.label || c.type}</span>
              </div>
            ))}
            <button type="button" className="cb-add-child"
              onClick={() => onAddChildBlock(block.id, def.defaultChildType || (def.allowedChildren?.[0] ?? ''))}>
              + {def.addChildLabel || 'Add child'}
            </button>
          </div>
        </Section>
      )}
      {parent && (
        <Section label="Parent">
          <p className="cb-help">{getDefinition(parent.type)?.label || parent.type}</p>
        </Section>
      )}
    </div>
  );
}

function Section({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <section className="cb-section">
      <h4>{label}</h4>
      {help && <p className="cb-help">{help}</p>}
      {children}
    </section>
  );
}

function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <label className="cb-field">
      <span className="cb-field-label">{label}</span>
      <span className="cb-field-control">{children}</span>
      {help && <span className="cb-field-help">{help}</span>}
    </label>
  );
}

/* Grouped, searchable theme picker. A native <select> doesn't scale to
   60+ entries; this gives every theme a swatch + family chip and lets
   the user filter the list. */
function ThemePickerField({
  themes, value, onChange,
}: { themes: ThemeEntry[]; value: string; onChange: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = findTheme(value);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? themes.filter((t) =>
          t.label.toLowerCase().includes(q) ||
          t.family.toLowerCase().includes(q) ||
          (t.variant || '').toLowerCase().includes(q))
      : themes;
    return groupThemes(list);
  }, [themes, query]);

  const familyKey = (fam: string) => fam.toLowerCase();
  return (
    <Field label="Theme" help={`${themes.length} themes`}>
      <button
        type="button"
        className="cb-theme-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="cb-theme-swatch" data-family={familyKey(current.family)} aria-hidden />
        <span className="cb-theme-trigger-label">{current.label}</span>
        <span className="cb-theme-trigger-caret">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="cb-theme-popover">
          <input
            type="search"
            className="cb-theme-search"
            placeholder={`Search ${themes.length} themes…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="cb-theme-list">
            {grouped.length === 0 && (
              <div className="cb-help" style={{ padding: 8 }}>No themes match.</div>
            )}
            {grouped.map(([family, items]) => (
              <div key={family} className="cb-theme-group">
                <div className="cb-theme-group-title">
                  {family} <span>{items.length}</span>
                </div>
                <div className="cb-theme-group-grid">
                  {items.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`cb-theme-option ${t.id === value ? 'is-active' : ''}`}
                      onClick={() => { onChange(t.id); setOpen(false); }}
                      title={t.label}
                    >
                      <span className="cb-theme-swatch" data-family={familyKey(t.family)} aria-hidden />
                      <span className="cb-theme-option-text">
                        <span className="cb-theme-option-name">{t.label}</span>
                        {t.variant && <span className="cb-theme-option-variant">{t.variant}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Field>
  );
}

/* Re-export for downstream symmetry. */
export { makeBlock };
